import * as Yup from 'yup';
import { UseFormSetValue, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Card,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import FormProvider, {
  RHFAutocomplete,
  RHFCheckbox,
  RHFRadioGroup,
  RHFSelect,
  RHFSwitch,
  RHFTextField,
} from 'components/hook-form';
import { IItemField, IPack } from '../../../../@types/package';
import { fNumber } from 'utils/formatNumber';
import { useEffect, useState } from 'react';
import { ObjectShape, TypeOfShape } from 'yup/lib/object';
import _ from 'lodash';
import { createOrder } from 'redux/slices/order-seeding';
import { useSelector } from 'redux/store';
import Iconify from 'components/iconify/Iconify';
import RHFReaction from 'components/hook-form/RHFReaction';
import { IErrorForlikeResponse } from '../../../../@types/error';
import { useRouter } from 'next/router';
import { useSnackbar } from 'notistack';
import { setShowLogin, setUser } from 'redux/slices/global';
import RHFPackRadioGroup from 'components/hook-form/RHFPackRadioGroup';
import { IService } from '../../../../@types/service';
import { checkMe } from 'redux/slices/user';
import OrderMultiple from './OrderMultiple';

// ----------------------------------------------------------------------
type Props = {
  packs: IPack[];
  service: IService;
};

type FormValuesProps = {
  uid: string;
  pack: string;
  quantity: number;
  [key: string]: string | number | boolean | object;
};

export default function OrderForm({ packs, service }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const router = useRouter();
  const currentPack = packs.length > 0 ? packs[0] : null;
  const { user } = useSelector((state) => state.global);
  const [schema, setSchema] = useState<TypeOfShape<ObjectShape>>({});
  const [pack, setPack] = useState<IPack | null | undefined>(currentPack);
  const [errors, setErrors] = useState<IErrorForlikeResponse[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [discount, setDiscount] = useState<number>(0);
  const [price, setPrice] = useState<number>(0);
  const [defaultValues, setDefaultValues] = useState<{ [x: string]: any }>({
    uid: '',
    pack: currentPack ? currentPack.id : '',
    quantity: 0,
  });
  const [quantity, setQuantity] = useState<IItemField | null | undefined>(null);
  const [multiple, setMultiple] = useState<boolean>(false);
  const [orderData, setOrderData] = useState<{ [x: string]: any } | null>(null);

  //#region Validate dữ liệu field tự động
  useEffect(() => {
    let sch: any = {
      pack: Yup.string().required('Vui lòng chọn một server'),
      uid: Yup.string().required('Nhập link hoặc UID'),
      quantity: Yup.number()
        .min(1, 'Vui lòng nhập số lượng')
        .min(
          pack?.quantitymin || 1,
          pack?.quantitymin ? `Số lượng lớn hơn bằng ${pack.quantitymin}` : 'Vui lòng nhập số lượng'
        )
        .max(
          pack?.quantitymax || 1000000000,
          pack?.quantitymax ? `Số lượng nhỏ hơn bằng ${pack.quantitymax}` : 'Vui lòng nhập số lượng'
        ),
    };

    if (service?.regexs?.length > 0) {
      sch.uid = Yup.string()
        .required('Nhập link hoặc UID')
        .test('regex-test', 'Link không đúng định dạng. Vui lòng nhập UID', function (value) {
          if (multiple) {
            const valids2: boolean[] = [];
            const uids = value?.split(`\n`) ?? [];
            for (const u of uids) {
              const valids1: boolean[] = [];
              for (let reg of service.regexs) {
                const regex = new RegExp(reg);
                //@ts-ignore
                if (regex.test(u)) {
                  valids1.push(true); // Trả về true nếu phù hợp với bất kỳ regex nào
                }
              }
              valids2.push(valids1.filter((f) => f).length > 0);
            }
            return valids2.filter((f) => f === false).length < 1;
          } else {
            for (let reg of service.regexs) {
              const regex = new RegExp(reg);
              //@ts-ignore
              if (regex.test(value)) {
                return true; // Trả về true nếu phù hợp với bất kỳ regex nào
              }
            }
            return false; // Trả về false nếu không phù hợp với bất kỳ regex nào
          }
        });
    }

    pack?.items.forEach((f) => {
      switch (f.type) {
        case 'number':
          sch[f.key] = Yup.number().required('Vui lòng nhập giá trị').min(0, 'Giá trị nhỏ nhất 0');
          setValue(f.key, 0);
          break;
        case 'boolean':
          sch[f.key] = Yup.boolean();
          setValue(f.key, false);
          break;
        case 'object':
          sch[f.key] = Yup.object().required('Vui lòng nhập giá trị');
          setValue(f.key, Object.assign({}));
          break;
        default:
          sch[f.key] = Yup.string().required('Vui lòng nhập giá trị');
          setValue(f.key, '');
      }
    });

    setSchema(sch);
    setPrice(pack?.pricesale || 0);
  }, [pack, multiple]);

  //#endregion

  useEffect(() => {
    setDiscount(user?.loyal?.discount || 0);
  }, [user]);

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(Yup.object().shape(schema)),
    defaultValues,
  });

  const {
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { isSubmitting },
  } = methods;

  //#region Kiểm tra thay đổi giá trị pack
  useEffect(() => {
    const packNew = packs.find((f) => f.id === watch('pack')) ?? packs[0];

    const _quantity = packNew?.items.find((f) => f.key === 'quantity');
    setPack(packNew);
    setQuantity(_quantity);
  }, [watch('pack'), packs, router.query.serviceId]);

  useEffect(() => {
    if (!pack) return;

    const { id, items } = pack;
    if (!items) return;

    const newValue: any = items.reduce(
      (acc: { [x: string]: any }, f) => {
        switch (f.type) {
          case 'number':
            acc[f.key] = 0;
            break;
          case 'boolean':
            acc[f.key] = false;
            break;
          case 'object':
            acc[f.key] = {};
            break;
          default:
            acc[f.key] = '';
        }
        return acc;
      },
      { pack: id, quantity: watch('quantity'), uid: watch('uid') }
    );
    setDefaultValues(newValue);
    reset(newValue, { keepErrors: true });
  }, [pack, reset, setDefaultValues, watch]);
  //#endregion

  //#region Kiểm tra giá trị thay đổi để tính tổng
  const formData: FormValuesProps = watch();
  useEffect(() => {
    if (formData?.comment) {
      setValue(
        'quantity',
        formData?.comment
          ?.toString()
          .split('\n')
          .filter((uid: string) => uid.trim() !== '').length
      );
    }
    let newTotal = formData.quantity * (pack?.pricesale ?? 1);
    if (formData.minute) newTotal *= Number(formData?.minute?.toString() ?? 1);

    for (const key in formData) {
      if (Object.prototype.hasOwnProperty.call(formData, key)) {
        const item = pack?.items
          .filter((f) => !['quantity', 'minute'].includes(f.key))
          .find((f) => f.key === key);
        if (item) {
          const value = item.value.find((f2) => f2.key === formData[key]);
          if (value) {
            newTotal *= value.price;
          }
        }
      }
    }

    setTotal(newTotal);
  }, [formData]);

  //#endregion

  const handleOder = async (values: FormValuesProps) => {
    if (!user) return setShowLogin(true);

    if (multiple) {
      setOrderData(values);
    } else {
      setErrors([]);
      const [_, error] = await createOrder(values);

      if (error) {
        setErrors(error.errors);
      } else {
        setErrors([]);
        enqueueSnackbar(
          `Bạn đã đặt thành công đơn hàng ${_?.code} với số tiền ${fNumber(
            (_?.total ?? 0) - (_?.pricediscount ?? 0)
          )} Coin`,
          { variant: 'success' }
        );
        const userMe = await checkMe();
        setUser(userMe);
        router.push(
          `/orderseeding/${router.query.group}/${router.query.serviceId}/list?page=0&limit=10&&search=`
        );
      }
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(handleOder)}>
      <Stack spacing={1}>
        <Stack spacing={1} sx={{ mb: 2 }}>
          {errors.map((error, index) => (
            <FormHelperText error key={index}>
              <li>{error.message}</li>
            </FormHelperText>
          ))}
        </Stack>
        <FormControlLabel
          label={
            multiple
              ? `Click chuyển về đặt 1 đơn: Số lượng UID bạn đang đặt là ${
                  watch('uid')?.split('\n').length
                }`
              : 'Nếu đặt nhiều đơn Click vào đây'
          }
          control={<Switch value={multiple} onChange={(e) => setMultiple(e.target.checked)} />}
        />

        <RHFTextField
          name="uid"
          label={multiple ? `Mỗi xuống dòng (enter) là một Link hoặc ID` : 'Link hoặc ID'}
          size="small"
          multiline={multiple}
          rows={5}
        />
        <RHFPackRadioGroup name="pack" options={packs} row={false} />
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems="flex-start"
          justifyContent="center"
          spacing={1}
        >
          {quantity ? (
            <RHFSelect
              name={quantity.key}
              label={quantity.label}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: false }}
            >
              {quantity.value.map((v) => (
                <MenuItem
                  key={v.key}
                  value={v.key}
                  sx={{
                    mx: 1,
                    my: 0.5,
                    borderRadius: 0.75,
                    typography: 'body2',
                    '&:first-of-type': { mt: 0 },
                    '&:last-of-type': { mb: 0 },
                  }}
                >
                  {`${v.name} - (giá x ${v.price})`}
                </MenuItem>
              ))}
            </RHFSelect>
          ) : (
            <RHFTextField
              disabled={
                pack?.items && pack?.items.filter((f) => ['comment'].includes(f.key))?.length > 0
              }
              name="quantity"
              label="Số lượng"
              type="number"
              size="small"
            />
          )}

          <TextField
            name="price"
            label="Giá"
            size="small"
            disabled
            value={fNumber(price)}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Button>Coin</Button>
                </InputAdornment>
              ),
              type: 'number',
            }}
          />
        </Stack>

        {_.chunk(PackItemFileds(pack, setValue), 2).map((m, index) => (
          <Stack
            key={index}
            direction={{ xs: 'column', md: 'row' }}
            alignItems="flex-start"
            justifyContent="center"
            spacing={1}
          >
            {m}
          </Stack>
        ))}
        <Card>
          <CardTotalCoin total={total} discount={discount} maxdiscount={pack?.maxdiscount ?? 0} />
        </Card>
      </Stack>
      <Stack alignItems="flex-end" sx={{ my: 1 }}></Stack>
      <LoadingButton
        fullWidth
        startIcon={<Iconify icon="mdi:cart-heart" />}
        color="primary"
        size="small"
        type="submit"
        variant="contained"
        loading={isSubmitting}
      >
        Tạo đơn
      </LoadingButton>
      <Dialog fullWidth maxWidth="lg" open={!!orderData}>
        <DialogTitle sx={{ pb: 2 }}>Tiến trình đặt nhiều đơn hàng</DialogTitle>
        <DialogContent sx={{ typography: 'body2', p: 2 }}>
          <OrderMultiple data={orderData} onClose={() => setOrderData(null)} />
        </DialogContent>
      </Dialog>
    </FormProvider>
  );
}
//----------------------------------------------------------------
function PackItemFileds(
  pack: IPack | undefined | null,
  setValue: UseFormSetValue<FormValuesProps>
) {
  if (!pack) return [];
  return pack.items
    .filter((f) => !['quantity'].includes(f.key))
    .map((m) => {
      switch (m.component) {
        case 'select':
          return (
            <RHFSelect
              key={m.key}
              name={m.key}
              label={m.label}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: false }}
            >
              {m.value.map((v) => (
                <MenuItem
                  key={v.key}
                  value={v.key}
                  sx={{
                    mx: 1,
                    my: 0.5,
                    borderRadius: 0.75,
                    typography: 'body2',
                    '&:first-of-type': { mt: 0 },
                    '&:last-of-type': { mb: 0 },
                  }}
                >
                  {`${v.name} - (giá x ${v.price})`}
                </MenuItem>
              ))}
            </RHFSelect>
          );
        case 'radiogroup':
          return (
            <Stack sx={{ width: '100%' }} key={m.key}>
              <RHFRadioGroup
                name={m.key}
                label={m.label}
                options={m.value.map((m2) => ({
                  label: `${m2.name} - (giá x ${m2.price})`,
                  value: m2.key,
                }))}
                row={true}
              />
            </Stack>
          );
        case 'reaction':
          return (
            <RHFReaction
              key={m.key}
              name={m.key}
              label={m.label}
              options={m.value.map((m2) => ({
                label: `${m2.name}`,
                value: m2.key,
              }))}
            />
          );
        case 'switch':
          return (
            <RHFSwitch
              key={m.key}
              name={m.key}
              label={m.label}
              labelPlacement="start"
              sx={{ mb: 1, mx: 0, width: 1, justifyContent: 'space-between' }}
            />
          );
        case 'checkbox':
          return <RHFCheckbox key={m.key} name={m.key} label={m.label} />;
        case 'autocomplete':
          return (
            <RHFAutocomplete
              key={m.key}
              name={m.key}
              freeSolo
              fullWidth
              size="small"
              onChange={(event, newValue) => {
                let newObj: any;
                if (typeof newValue === 'string')
                  newObj = {
                    key: '0',
                    name: newValue,
                    price: 1,
                  };
                else newObj = newValue;

                setValue(m.key, newObj?.key ?? '');
              }}
              getOptionLabel={(option) =>
                typeof option === 'object'
                  ? `${option.name} - (giá x ${option.price})`
                  : option.toString()
              }
              isOptionEqualToValue={(option, value) => option.key === value.key}
              options={m.value.map((option) => option)}
              renderOption={(props, option, { selected }) => (
                <li key={option.key} {...props}>
                  <Checkbox size="small" checked={selected} />
                  {`${option.name} - (giá x ${option.price})`}
                </li>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.key}
                    size="small"
                    label={`${option.name} - (giá x ${option.price})`}
                  />
                ))
              }
              renderInput={(params) => <TextField label={m.label} {...params} />}
            />
          );
        case 'multiple':
          return (
            <RHFTextField
              key={m.key}
              multiline
              minRows={3}
              fullWidth
              name={m.key}
              label={m.label}
              size="small"
            />
          );
        default:
          return <RHFTextField key={m.key} fullWidth name={m.key} label={m.label} size="small" />;
      }
    });
}

export function CardTotalCoin({
  total,
  discount,
  maxdiscount,
}: {
  total: number;
  discount: number;
  maxdiscount: number;
}) {
  const priceDiscount = total * (discount / 100);
  const totalPriceDiscount =
    maxdiscount > 0 && maxdiscount <= priceDiscount ? maxdiscount : priceDiscount;

  const totalPrice = total - totalPriceDiscount;
  const formattedTotalPrice = fNumber(totalPrice);

  const formattedDiscountedPrice = fNumber(totalPriceDiscount);
  const discountText =
    maxdiscount > 0 && maxdiscount <= priceDiscount ? totalPriceDiscount : `${discount} %`;
  return (
    <Stack
      sx={{ backgroundColor: '#F3FAFF', p: 2 }}
      direction="column"
      alignItems="center"
      justifyContent="center"
      spacing={1}
    >
      <Typography color="primary" variant="subtitle2">
        {`Tổng Thanh Toán: ${fNumber(
          total
        )} - ${formattedDiscountedPrice} = ${formattedTotalPrice} Coin`}
      </Typography>
      {maxdiscount > 0 && maxdiscount <= priceDiscount ? (
        <Typography color="primary" variant="body2">
          {`Bạn đã được giảm giá tối đa ${formattedDiscountedPrice} Coin cho đơn hàng.`}
        </Typography>
      ) : (
        <Typography color="primary" variant="body2">
          {`Bạn đã được chiết khấu ${discountText} giảm giá ${formattedDiscountedPrice} Coin cho đơn hàng.`}
        </Typography>
      )}
    </Stack>
  );
}
